import { useState, useCallback, useEffect } from 'react';

/**
 * useLLMPreferences — Persists LLM provider selection and related settings
 * per client to localStorage. When the provider changes, the caller can
 * react to the change and refresh the UI.
 *
 * Storage key: llm_prefs_{clientId}
 */

const LLM_PROVIDERS = [
  { id: 'gemini', label: 'Gemini (Google) — Fastest', icon: '🔷', model: 'gemini-1.5-pro', speed: 'fast' },
  { id: 'claude', label: 'Claude (Anthropic) — Moderate', icon: '🟣', model: 'claude-3-sonnet', speed: 'moderate' },
  { id: 'bedrock', label: 'Bedrock (AWS) — Moderate', icon: '🟠', model: 'bedrock-claude-3', speed: 'moderate' },
  { id: 'chatgpt', label: 'ChatGPT (OpenAI) — Slow ⚠️', icon: '🟢', model: 'gpt-4o', speed: 'slow', warning: 'Up to 5 min. Frequent timeout risk for DDLS reports.' },
];

const DEFAULT_PREFS = {
  provider: 'gemini',
  customQuestions: [],
  llmSetup: {},
  lastUpdated: null,
};

function getStorageKey(clientId) {
  return `llm_prefs_${clientId}`;
}

export function useLLMPreferences(clientId) {
  const [prefs, setPrefs] = useState(() => {
    if (!clientId) return DEFAULT_PREFS;
    try {
      const stored = localStorage.getItem(getStorageKey(clientId));
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_PREFS, ...parsed };
      }
    } catch { /* ignore */ }
    return DEFAULT_PREFS;
  });

  // Re-load when clientId changes
  useEffect(() => {
    if (!clientId) { setPrefs(DEFAULT_PREFS); return; }
    try {
      const stored = localStorage.getItem(getStorageKey(clientId));
      if (stored) {
        setPrefs({ ...DEFAULT_PREFS, ...JSON.parse(stored) });
      } else {
        setPrefs(DEFAULT_PREFS);
      }
    } catch {
      setPrefs(DEFAULT_PREFS);
    }
  }, [clientId]);

  const persist = useCallback((updated) => {
    if (!clientId) return;
    const withTimestamp = { ...updated, lastUpdated: new Date().toISOString() };
    setPrefs(withTimestamp);
    localStorage.setItem(getStorageKey(clientId), JSON.stringify(withTimestamp));
  }, [clientId]);

  const setProvider = useCallback((providerId) => {
    const updated = { ...prefs, provider: providerId };
    persist(updated);
  }, [prefs, persist]);

  const setCustomQuestions = useCallback((questions) => {
    const updated = { ...prefs, customQuestions: questions };
    persist(updated);
  }, [prefs, persist]);

  const setLlmSetup = useCallback((setup) => {
    const updated = { ...prefs, llmSetup: { ...prefs.llmSetup, ...setup } };
    persist(updated);
  }, [prefs, persist]);

  const provider = LLM_PROVIDERS.find(p => p.id === prefs.provider) || LLM_PROVIDERS[0];

  return {
    provider,
    providerId: prefs.provider,
    providers: LLM_PROVIDERS,
    customQuestions: prefs.customQuestions,
    llmSetup: prefs.llmSetup,
    setProvider,
    setCustomQuestions,
    setLlmSetup,
    prefs,
  };
}

export { LLM_PROVIDERS };
export default useLLMPreferences;
